import { Moment } from 'moment';

import { translate } from '@front/i18n';

import moment from '../utils/moment';
import { isStringEmpty } from '../utils/strings';
import { Address } from '../types/commons';

import { isValidDate } from './validators/date';
import { validateEmail } from './validators/email';
import { validateBic, validateIban } from './validators/iban';
import { validateTime } from './validators/time';
import { validateZipCode } from './validators/zip-code';
import { validateAddressLegacy, validateAddress } from './validators/address';
import { PasswordRuleType } from './validators/password/type';
import { validatePensionInsurance } from './validators/pension-insurance';
import { emailRegexp } from './validators/email-schema';

const t = translate('inputs');

type TranslationValues = Record<string, string | number>;

const message = (inputName: string, error: string, values?: TranslationValues) =>
  t(`.${inputName}.errors.${error}`, values);

export type Validator<V> = (value: V) => string | undefined;

export const validator =
  <V>(inputName: string, error: string, rule: (value: V) => boolean, values?: TranslationValues): Validator<V> =>
  (value) =>
    rule(value) ? undefined : message(inputName, error, values);

export const compose =
  <V>(...validators: Validator<V>[]) =>
  (value: V) =>
    validators.reduce<string | undefined>(
      (error, someValidator) => error || (someValidator ? someValidator(value) : undefined),
      undefined,
    );

export const required = (inputName: string) =>
  validator<string | Moment | number | undefined>(inputName, 'required', (value) =>
    typeof value === 'string' ? !isStringEmpty(value) : !!value,
  );

export const regexp = (inputName: string, exp: RegExp) =>
  validator<string | undefined>(inputName, 'invalid', (value) => !!(value && value.match(exp)));

export const greaterThan = (inputName: string, min: number) =>
  validator<number | undefined>(inputName, 'greater_than', (value) => !!(value && value > min), {
    value: min,
  });

export const greaterThanOrEqual = (inputName: string, min: number) =>
  validator<number | undefined>(inputName, 'greater_than_or_equal', (value) => !!(value && value >= min), {
    value: min,
  });

export const lessThan = (inputName: string, max: number) =>
  validator<number | undefined>(inputName, 'less_than', (value) => !!(value && value < max), {
    value: max,
  });

export const lessThanOrEqual = (inputName: string, max: number) =>
  validator<number | undefined>(inputName, 'less_than_or_equal', (value) => !!(value && value <= max), {
    value: max,
  });

export const dateValidator = (inputName: string) =>
  validator<string | undefined>(inputName, 'valid_date', isValidDate);

export const timeValidator = (inputName: string) =>
  validator<string | undefined>(inputName, 'valid_time', validateTime);

const compareDates = (
  inputName: string,
  comparatorName: string,
  limit: Moment,
  getFormat: () => string,
  comparator: (value: Moment, limit: Moment) => boolean,
) => {
  const rule = (value?: string | Moment) => {
    const valueMoment = value instanceof moment ? (value as Moment) : moment(value, getFormat());
    return comparator(valueMoment, limit);
  };

  return validator<string | Moment>(inputName, comparatorName, rule, {
    value: limit.format(getFormat()),
  });
};

export const beforeThan = (inputName: string, max: Moment, getFormat: () => string) =>
  compareDates(inputName, 'before_than', max, getFormat, (value: Moment, limit: Moment) => value.isBefore(limit));

export const afterThan = (inputName: string, min: Moment, getFormat: () => string) =>
  compareDates(inputName, 'after_than', min, getFormat, (value: Moment, limit: Moment) => value.isAfter(limit));

export const beforeThanOrEqual = (inputName: string, max: Moment, getFormat: () => string) =>
  compareDates(inputName, 'before_than_or_equal', max, getFormat, (value: Moment, limit: Moment) =>
    value.isSameOrBefore(limit),
  );

export const afterThanOrEqual = (inputName: string, min: Moment, getFormat: () => string) =>
  compareDates(inputName, 'after_than_or_equal', min, getFormat, (value: Moment, limit: Moment) =>
    value.isSameOrAfter(limit),
  );

export const passwordRules: Record<PasswordRuleType, (val: string) => boolean> = {
  length: (value: string) => value.length >= 8,
  upperCase: (value: string) => /[A-Z]/.test(value),
  lowerCase: (value: string) => /[a-z]/.test(value),
  number: (value: string) => /[0-9]/.test(value),
  symbol: (value: string) => /[!@#$%^&*()_+\-=[\]{}|'"/\\.,`<>:;?~]/.test(value),
};

export const isValidPassword = (value: string | undefined) => {
  if (!value) return false;
  if (!passwordRules.length(value)) return false;
  if (!passwordRules.upperCase(value)) return false;
  if (!passwordRules.lowerCase(value)) return false;
  if (!passwordRules.number(value)) return false;
  if (!passwordRules.symbol(value)) return false;
  return true;
};

export const validateTooShortEmail = (value: string | undefined) => !emailRegexp.tooShort.test(value || '');

export const validatePassword = (inputName: string) =>
  validator<string | undefined>(inputName, 'invalid', (value) => isValidPassword(value));

export const tooShort = (inputName: string, minLength: number) =>
  validator<string | undefined>(inputName, 'too_short', (value) => !!(value && value.length > minLength), {
    value: minLength,
  });

export const tooLong = (inputName: string, maxLength: number) =>
  validator<string | undefined>(inputName, 'too_long', (value) => !!(value && value.length <= maxLength), {
    max: maxLength,
  });

export const zipCodeValidator = (inputName: string) =>
  validator<string | undefined>(inputName, 'invalid', validateZipCode);

export const addressValidator = (inputName: string) =>
  validator<Partial<Address> | undefined>(inputName, 'invalid', validateAddress);

// @deprecated only used in pro-details
export const addressValidatorLegacy = (inputName: string) =>
  validator<Partial<Address> | undefined>(inputName, 'invalid', validateAddressLegacy);

export const internationalPhoneValidator = (inputName: string, isPhoneValid: boolean) =>
  compose<string | undefined>(required(inputName), () => (isPhoneValid ? undefined : t('.phone.errors.invalid')));

export const emailValidator = (inputName: string) =>
  validator<string | undefined>(inputName, 'invalid', validateEmail);

export const tooShortEmailValidator = (inputName: string) =>
  validator<string | undefined>(inputName, 'too_short', validateTooShortEmail);

export const ibanValidator = (inputName: string) => validator<string | undefined>(inputName, 'invalid', validateIban);

export const bicValidator = (inputName: string) => validator<string | undefined>(inputName, 'invalid', validateBic);

export const pensionInsuranceValidator = (inputName: string) =>
  validator<string | undefined>(inputName, 'invalid', validatePensionInsurance);
